/**
 * This file handles the update for new electron packages.
 * https://electronjs.org/docs/api/auto-updater
 */
import { autoUpdaterWrapper } from "./autoUpdaterWrapper";
import { getOrCreateMainLogger } from "../logger/main";
import ChildProcess from "child_process";
import path from "path";
import fs from "fs";
import { dialog } from "electron";
import { getConfig } from "./Config";

const logger = getOrCreateMainLogger();

// How often to look for updates
const CHECK_UPDATE_FREQUENCY = 60000;

class UpdateManager {
	feedURL: string | null;

	updateTimeout: number;

	signalUpdateAvailable: Function | null;

	setUpdateAvailable: Function | null;

	updateChecker: ReturnType<typeof setTimeout> | undefined;

	/**
	 * @param {*} feedURL - The server url to look for updates
	 * @param {*} updateTimeout  - How often to look for updates
	 */
	constructor() {
		this.feedURL = null;
		this.updateTimeout = CHECK_UPDATE_FREQUENCY;

		// This function is set by the MainSystem, it accepts a function from auto updater and invokes it to apply the updates, close all the applications, and restart the application.
		this.signalUpdateAvailable = null;
		this.setUpdateAvailable = null;
		this._startUpdater = this._startUpdater.bind(this);
	}

	/**
	 * Set the update URL
	 * @param {string} feedUrl - The server URL to look for updates
	 */
	setFeedUrl(feedUrl: string) {
		this.feedURL = feedUrl;
	}

	usePreloadUpdater(usePreloader: boolean) {
		autoUpdaterWrapper.downloadViaElectron(usePreloader);
	}

	/**
	 * Set how often we should check for updates
	 * @param {*} timeInMs - The frequency we check for updates in milliseconds
	 */
	setUpdateFrequency(timeInMs: number) {
		this.updateTimeout = timeInMs;
	}

	/**
	 * Set the function to request a restart of the application with the updates
	 * @param {*} signalUpdateAvailable The function (set by MainSystem.js) to apply the updates and restart the application.
	 */
	setOnRestartAndUpdateRequested(signalUpdateAvailable: Function) {
		this.signalUpdateAvailable = signalUpdateAvailable;
	}

	getUpdateAvailabilityPromise(): Promise<boolean> {
		return autoUpdaterWrapper.getUpdateAvailabilityPromise();
	}

	/**
	 * Request a restart the application to apply the updates.
	 */
	requestRestart() {
		if (this.signalUpdateAvailable) {
			logger.info("Requesting restart");
			this.signalUpdateAvailable();
		} else {
			logger.error("signalUpdateAvailable function is not set for the UpdateManager");
		}
	}

	/**
	 * Check for updates according to the frequency we set on the UpdateManager
	 */
	_startUpdater() {
		this.updateChecker = setTimeout(() => {
			logger.info("Checking for updates");
			autoUpdaterWrapper.checkForUpdates();
		}, this.updateTimeout);
	}

	/**
	 * Sets up all of the update listeners
	 */
	listenForNewUpdates() {
		if (!this.feedURL) {
			logger.error("FeedURL is not set for the auto updater. Cannot set up listener to listen for updates");
			return;
		}
		autoUpdaterWrapper.setFeedURL({ url: this.feedURL });
		logger.log("update url:", this.feedURL);
		autoUpdaterWrapper.on("update-downloaded", () => {
			logger.log("update-downloaded - prompting to restart");
			this.requestRestart();
		});
		autoUpdaterWrapper.on("update-available", () => {
			logger.log("Update available. The update will be downloaded automatically in the background.");
		});
		autoUpdaterWrapper.on("before-quit-for-update", () => {
			logger.debug("before-quit-for-update");
		});
		autoUpdaterWrapper.on("checking-for-update", () => {
			logger.info("Checking for update");
			if (this.updateChecker) {
				clearTimeout(this.updateChecker);
			}
		});
		autoUpdaterWrapper.on("update-not-available", () => {
			logger.info("Update not available");
			this._startUpdater();
		});
		autoUpdaterWrapper.on("error", (message) => {
			logger.error(`There was a problem updating the application ${message}`);
		});
		autoUpdaterWrapper.checkForUpdates();
	}
}

export const updateManager = new UpdateManager();

export function handleSquirrelEvents({ app, userDataPath }: { app: any; userDataPath: string }) {
	// If we don't have an install event then we're just running the application
	if (process.argv.length === 1) {
		return false;
	}
	const appFolder = path.resolve(process.execPath, "..");
	const rootAtomFolder = path.resolve(appFolder, "..");
	const updateDotExe = path.resolve(path.join(rootAtomFolder, "Update.exe"));
	const exeName = path.basename(process.execPath);
	logger.debug(`Update exe path: ${exeName}`);
	/**
	 *  Here we either spawn the application or the electron updater
	 * @param {*} command  - The application to spawn
	 * @param {*} args  - the command line arguments
	 */
	const spawn = function (command: any, args: any) {
		let spawnedProcess;
		try {
			spawnedProcess = ChildProcess.spawn(command, args, { detached: true });
		} catch (error: any) {
			dialog.showErrorBox("Error in setupEvents/spawn", `${error?.message || error}`);
		}

		return spawnedProcess;
	};

	/**
	 * This will run the electron updater
	 * @param {*} args - Command line arguments meant for the updater
	 */
	const spawnUpdate = function (args: any) {
		return spawn(updateDotExe, args);
	};

	const maybeDeleteUserData = getConfig({ suppressLog: true })?.deleteUserDataOnUninstall ?? false;
	const squirrelEvent = process.argv[1];
	switch (squirrelEvent) {
		case "--squirrel-firstrun":
			spawnUpdate(["--createShortcut", exeName]);
			return false;
		case "--squirrel-install":
		case "--squirrel-updated":
			spawnUpdate(["--createShortcut", exeName]);
			setTimeout(app.quit, 1000);
			return true;
		case "--squirrel-uninstall":
			// Undo anything you did in the --squirrel-install and
			// --squirrel-updated handlers
			// Remove desktop and start menu shortcuts
			spawnUpdate(["--removeShortcut", exeName]);
			try {
				if (maybeDeleteUserData) {
					logger.log("DELETING USER DATA PATH", userDataPath);
					fs.rmSync(userDataPath, { recursive: true });
				}
			} catch (error) {
				logger.error("Error deleting userdata folder", error);
			}
			setTimeout(app.quit, 1000);
			return true;

		case "--squirrel-obsolete":
			// This is called on the outgoing version of your app before
			// we update to the new version - it's the opposite of
			// --squirrel-updated.
			app.quit();
			return true;

		default: // do nothing
	}
	return false;
}
